/**
 * Create New Version Page — single continuous chat view.
 *
 * Everything renders inline in one scrollable area:
 * greeting → approach cards → user messages → assistant messages →
 * thinking → workflow progress → review artifacts → done/error.
 *
 * The input bar is pinned to the bottom and visible during
 * approach / chat / planning phases.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, BarChart3, CheckCircle2, ChevronRight, FlaskConical, Loader2, Pencil, Sparkles, XCircle } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCreateVersion } from '@/hooks/useCreateVersion';
import { useOrganization } from '@/hooks/useOrganization';
import { useToast } from '@/hooks/use-toast';
import salesAgentService from '@/lib/api/salesAgent';

import ApproachSelector from '@/components/create-version/ApproachSelector';
import ChatMessage from '@/components/create-version/ChatMessage';
import ChatInput from '@/components/create-version/ChatInput';
import OptionPills from '@/components/create-version/OptionPills';
import ThinkingIndicator from '@/components/create-version/ThinkingIndicator';
import WorkflowProgress from '@/components/create-version/WorkflowProgress';
import VersionCreatedCard from '@/components/create-version/VersionCreatedCard';
import EvalScoresCard from '@/components/create-version/EvalScoresCard';
import InsightSuggestionPills from '@/components/create-version/InsightSuggestionPills';
import EvalScoresSidebar from '@/components/create-version/EvalScoresSidebar';
import PromptChangesSidebar from '@/components/create-version/PromptChangesSidebar';
import EvalReportSidebar from '@/components/create-version/EvalReportSidebar';
import { messageVariantsLeft, easeOut } from '@/components/create-version/animations';
import { HobbesAvatar } from '@/components/interview/HobbesAvatar';

const BootstrapGenerationPanel: React.FC<{ organizationId: string }> = ({ organizationId }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [jobId, setJobId] = useState<string | null>(null);

  const generateMutation = useMutation({
    mutationFn: async () => {
      const normalizedWebsiteUrl = websiteUrl.trim();
      if (!normalizedWebsiteUrl) {
        throw new Error('Website URL is required');
      }

      const response = await salesAgentService.startConfigGeneration(organizationId, {
        company_website_url: normalizedWebsiteUrl,
      });

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to start config generation');
      }

      return response.data;
    },
    onSuccess: (data) => {
      setJobId(data.job_id);
      toast({
        title: 'Generation started',
        description: 'We’re generating your initial agent configuration now.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Unable to start generation',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const { data: generationStatus } = useQuery({
    queryKey: ['sales-agent-config-generation', organizationId, jobId],
    queryFn: async () => {
      if (!jobId) return null;
      const response = await salesAgentService.getConfigGenerationStatus(organizationId, jobId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to load generation status');
      }
      return response.data;
    },
    enabled: !!jobId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'completed' || status === 'failed' ? false : 2000;
    },
  });

  useEffect(() => {
    if (generationStatus?.status === 'completed') {
      queryClient.invalidateQueries({ queryKey: ['sales-agent', organizationId] });
      navigate('/agents/configure');
    }
  }, [generationStatus?.status, navigate, organizationId, queryClient]);

  const statusMessage = (() => {
    switch (generationStatus?.status) {
      case 'pending':
        return 'Getting your agent ready...';
      case 'generating':
        return 'Creating your agent from your company website...';
      case 'applying':
        return 'Applying your agent settings...';
      case 'failed':
        return generationStatus.error_message || 'We couldn’t create your agent. Please try again.';
      default:
        return "We'll generate your first version. From there, you can review it, give feedback, and iterate.";
    }
  })();

  return (
    <>
      <Helmet>
        <title>Create Agent | Hobbes</title>
      </Helmet>

      <div className="flex flex-col h-full min-h-0">
        <div className="px-4 sm:px-6 md:px-8 py-4 border-b border-border/40">
          <button
            onClick={() => navigate('/agents')}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-heading transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="font-medium text-heading text-base">Create Agent</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 sm:px-6 md:px-8 py-8">
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardHeader className="space-y-3">
                <CardTitle className="text-xl">Create your first agent</CardTitle>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  We’ll use your website along with any uploaded background materials and interview sessions to create your agent.
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="company_website_url" className="text-sm font-medium">Company Website</Label>
                  <Input
                    id="company_website_url"
                    type="url"
                    placeholder="https://www.yourcompany.com"
                    value={websiteUrl}
                    onChange={(event) => setWebsiteUrl(event.target.value)}
                    disabled={generateMutation.isPending || (!!jobId && generationStatus?.status !== 'failed')}
                    className="h-10"
                  />
                  <p className="text-xs text-muted-foreground">
                    We’ll start by pulling context from your website.
                  </p>
                </div>

                <div className="rounded-xl border border-border/60 bg-muted/20 p-4">
                  <div className="flex items-start gap-3">
                    {jobId && generationStatus?.status !== 'failed' ? (
                      <Loader2 className="mt-0.5 h-4 w-4 animate-spin text-primary" />
                    ) : generationStatus?.status === 'failed' ? (
                      <Sparkles className="mt-0.5 h-4 w-4 text-destructive" />
                    ) : (
                      <CheckCircle2 className="mt-0.5 h-4 w-4 text-muted-foreground" />
                    )}
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-heading">
                        {jobId ? 'Generation status' : 'What happens next'}
                      </p>
                      <p className="text-sm text-muted-foreground">{statusMessage}</p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  <Button
                    type="button"
                    onClick={() => generateMutation.mutate()}
                    disabled={generateMutation.isPending || !websiteUrl.trim() || (!!jobId && generationStatus?.status !== 'failed')}
                  >
                    {(generateMutation.isPending || (jobId && generationStatus?.status !== 'failed')) && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    {generationStatus?.status === 'failed' ? 'Retry Create Agent' : 'Create Agent'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/agents')}
                  >
                    Back to Agent Versions
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

const CreateVersionComposer: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const baseVersion = searchParams.get('version') ? Number(searchParams.get('version')) : undefined;
  const {
    state,
    selectApproach,
    startDirectSession,
    confirmDraftPlan,
    sendMessage,
    applyPrompt,
    removeChange,
    updateChangeText,
    cancelWorkflow,
    reset,
  } = useCreateVersion(baseVersion);

  const bottomRef = useRef<HTMLDivElement>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [insightsSidebarOpen, setInsightsSidebarOpen] = useState(false);
  const [changesOpen, setChangesOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  // Auto-scroll to bottom when content changes
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [state.messages, state.phase, state.isSending]);

  const showInput =
    state.phase === 'chat' ||
    state.phase === 'planning' ||
    state.phase === 'confirm';
  const showWorkflowInsightsSidebar =
    state.intent !== 'targeted_revision' || state.phase === 'scores_ready';
  const workflowSidebarScores =
    state.phase === 'scores_ready'
      ? (state.workflowStatus?.post_change_scores ?? null)
      : (state.workflowStatus?.baseline_scores ?? null);
  const workflowSidebarPatterns =
    state.intent === 'targeted_revision' && state.phase === 'scores_ready'
      ? []
      : (state.reviewArtifacts?.failure_patterns ?? []);
  const postChangeScoreAverages = state.workflowStatus?.post_change_scores?.score_averages ?? {};
  const postChangeRubricCount = Object.values(postChangeScoreAverages).filter(
    (value) => value != null,
  ).length;

  return (
    <>
      <Helmet>
        <title>Create New Version | Hobbes</title>
      </Helmet>

      <div className="flex flex-col h-full min-h-0">
        {/* ── Header ─────────────────────────────────────────────── */}
        <div className="px-4 sm:px-6 md:px-8 py-4 border-b border-border/40">
          <button
            onClick={() => navigate('/agents')}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-heading transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="font-medium text-heading text-base">Create New Version</span>
          </button>
        </div>

        {/* ── Scrollable chat area ───────────────────────────────── */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 md:px-8 py-6">
          <div className="max-w-2xl mx-auto space-y-5">
            {/* Greeting — always visible */}
            <motion.div
              variants={messageVariantsLeft}
              initial="initial"
              animate="animate"
              transition={easeOut}
              className="flex justify-start"
            >
              <div className="flex gap-3 max-w-[80%]">
                <HobbesAvatar size="sm" />
                <div className="rounded-2xl rounded-tl-md bg-muted/50 px-4 py-2.5 space-y-1">
                  <p className="text-sm text-body leading-relaxed">
                    Let's create a new version of your agent. Pick an approach below.
                  </p>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Each option generates an updated prompt you can review before publishing.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Approach cards — only in approach phase */}
            {state.phase === 'approach' && (
              <ApproachSelector
                onSelect={selectApproach}
                onDirectRun={startDirectSession}
                disabled={state.isSending}
              />
            )}

            {/* Score-driven flow: first message, then card+pills, then remaining messages */}
            {/* Non-score-driven flow: all messages rendered sequentially */}
            {(() => {
              const showInsightsCard =
                state.intent === 'score_driven' &&
                state.evalRubrics.length > 0 &&
                !state.insightsLoading;

              if (showInsightsCard && state.messages.length > 0) {
                const firstMsg = state.messages[0];
                const rest = state.messages.slice(1);
                return (
                  <>
                    <ChatMessage
                      key={firstMsg.id}
                      role={firstMsg.role}
                      content={firstMsg.content}
                    />
                    <ChatMessage
                      key="insights-intro"
                      role="assistant"
                      content="Here's how your agent scored in recent conversations."
                    />
                    <EvalScoresCard
                      rubrics={state.evalRubrics}
                      evalSetName={state.evalSetName}
                      onExpand={() => setInsightsSidebarOpen(true)}
                    />
                    {rest.map((msg) => (
                      <ChatMessage
                        key={msg.id}
                        role={msg.role}
                        content={msg.content}
                      />
                    ))}
                  </>
                );
              }

              return state.messages.map((msg) => (
                <ChatMessage
                  key={msg.id}
                  role={msg.role}
                  content={msg.content}
                />
              ));
            })()}

            {/* Option pills — show after first assistant reply in targeted_revision flow */}
            {state.phase === 'chat' &&
              state.intent !== 'score_driven' &&
              !state.isSending &&
              state.rubricNames.length > 0 &&
              state.messages.filter((m) => m.role === 'assistant').length === 1 &&
              state.messages[state.messages.length - 1]?.role === 'assistant' && (
                <OptionPills
                  options={state.rubricNames}
                  onSelect={(option) => sendMessage(option)}
                />
              )}

            {/* Suggestion pills — show after latest assistant reply when labels available */}
            {state.phase === 'chat' &&
              !state.isSending &&
              state.suggestionLabels.length > 0 &&
              state.messages[state.messages.length - 1]?.role === 'assistant' && (
                <InsightSuggestionPills
                  labels={state.suggestionLabels}
                  onSelect={(label) => sendMessage(label)}
                  disabled={state.isSending}
                />
              )}

            {/* Thinking indicator — while waiting for planner, planning, or loading insights */}
            {(state.isSending || state.phase === 'planning' || state.insightsLoading) && (
              <ThinkingIndicator />
            )}

            {state.phase === 'confirm' && state.draftPlan && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                className="rounded-xl border border-border/60 bg-card p-4 space-y-4"
              >
                <div className="space-y-2">
                  <p className="text-sm font-medium text-heading">
                    Ready to generate the prompt changes
                  </p>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    I&apos;ll generate changes for <span className="text-heading">{state.draftPlan.focus_summary}</span> and keep <span className="text-heading">{state.draftPlan.unchanged_summary.toLowerCase()}</span>.
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    size="sm"
                    className="bg-primary hover:bg-primary/90"
                    onClick={() => confirmDraftPlan()}
                    disabled={state.isSending}
                  >
                    Apply Changes
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Workflow progress — running phase */}
            {state.phase === 'running' && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                className="space-y-3"
              >
                <WorkflowProgress
                  status={state.workflowStatus?.status ?? 'pending'}
                />
                <div className="flex justify-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground hover:text-red-600 dark:hover:text-red-400"
                    onClick={cancelWorkflow}
                  >
                    <XCircle className="mr-1.5 h-3.5 w-3.5" />
                    Cancel Workflow
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Review artifacts — review phase */}
            {state.phase === 'review' && state.reviewArtifacts && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="space-y-5"
              >
                {/* Feedback summary */}
                {state.reviewArtifacts.feedback_summary && (
                  <div className="rounded-xl border border-border/60 bg-card p-4">
                    <p className="text-sm text-body leading-relaxed">
                      {state.reviewArtifacts.feedback_summary}
                    </p>
                  </div>
                )}

                {/* Eval scores sidebar trigger */}
                {showWorkflowInsightsSidebar &&
                  (state.reviewArtifacts.failure_patterns.length > 0 ||
                  state.workflowStatus?.baseline_scores?.score_averages) && (
                  <motion.button
                    type="button"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={easeOut}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setSidebarOpen(true)}
                    className="w-full flex items-center gap-3 rounded-xl border border-border/60 bg-card px-4 py-3 text-left hover:bg-muted/30 transition-colors"
                  >
                    <BarChart3 className="h-4 w-4 text-primary shrink-0" />
                    <div className="min-w-0 flex-1">
                      <span className="text-sm font-medium text-heading">
                        View Identified Patterns and Scores
                      </span>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {state.reviewArtifacts.failure_patterns.length > 0 &&
                          `${state.reviewArtifacts.failure_patterns.length} pattern${state.reviewArtifacts.failure_patterns.length > 1 ? 's' : ''} identified`}
                        {state.reviewArtifacts.failure_patterns.length > 0 &&
                          state.workflowStatus?.baseline_scores?.score_averages &&
                          ' · '}
                        {state.workflowStatus?.baseline_scores?.score_averages &&
                          `${Object.values(state.workflowStatus.baseline_scores.score_averages).filter((v) => v != null).length} rubrics scored`}
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                  </motion.button>
                )}

                {/* Prompt changes sidebar trigger */}
                {state.reviewArtifacts.proposed_changes.length > 0 && (
                  <motion.button
                    type="button"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ ...easeOut, delay: 0.08 }}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setChangesOpen(true)}
                    className="w-full flex items-center gap-3 rounded-xl border border-border/60 bg-card px-4 py-3 text-left hover:bg-muted/30 transition-colors"
                  >
                    <Pencil className="h-4 w-4 text-primary shrink-0" />
                    <div className="min-w-0 flex-1">
                      <span className="text-sm font-medium text-heading">
                        View Prompt Changes
                      </span>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {state.reviewArtifacts.proposed_changes.length} Section{state.reviewArtifacts.proposed_changes.length !== 1 ? 's' : ''} Modified
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                  </motion.button>
                )}

                {/* Review action bar */}
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ ...easeOut, delay: 0.16 }}
                  className="flex items-center gap-3 pt-2">
                  {state.intent === 'targeted_revision' ? (
                    <>
                      <Button
                        size="sm"
                        className="bg-primary hover:bg-primary/90"
                        onClick={() => applyPrompt(state.reviewArtifacts?.updated_prompt ?? undefined, 'save')}
                      >
                        Save Version
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => applyPrompt(state.reviewArtifacts?.updated_prompt ?? undefined, 'run_scores')}
                      >
                        <FlaskConical className="h-3.5 w-3.5 mr-1.5" />
                        Run Simulations & Scores
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        className="bg-primary hover:bg-primary/90"
                        onClick={() => applyPrompt(state.reviewArtifacts?.updated_prompt ?? undefined, false)}
                      >
                        Save Version
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => applyPrompt(state.reviewArtifacts?.updated_prompt ?? undefined, true)}
                      >
                        <FlaskConical className="h-3.5 w-3.5 mr-1.5" />
                        Run A/B Tests
                      </Button>
                    </>
                  )}
                </motion.div>
              </motion.div>
            )}


            {/* Scores ready — prompt scored after changes, awaiting next decision */}
            {state.phase === 'scores_ready' && state.reviewArtifacts && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="space-y-5"
              >
                <div className="rounded-xl border border-border/60 bg-card p-4 space-y-2">
                  <p className="text-sm font-medium text-heading">
                    Simulations and scoring are complete
                  </p>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {state.workflowStatus?.post_change_scores?.scored_items ?? 0} of{' '}
                    {state.workflowStatus?.post_change_scores?.total_items ?? 0} items were scored across{' '}
                    {postChangeRubricCount} rubric{postChangeRubricCount === 1 ? '' : 's'}.
                  </p>
                </div>

                {showWorkflowInsightsSidebar &&
                  state.workflowStatus?.post_change_scores?.score_averages && (
                  <motion.button
                    type="button"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ ...easeOut, delay: 0.08 }}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setSidebarOpen(true)}
                    className="w-full flex items-center gap-3 rounded-xl border border-border/60 bg-card px-4 py-3 text-left hover:bg-muted/30 transition-colors"
                  >
                    <BarChart3 className="h-4 w-4 text-primary shrink-0" />
                    <div className="min-w-0 flex-1">
                      <span className="text-sm font-medium text-heading">
                        View Simulation Scores
                      </span>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {postChangeRubricCount} rubric{postChangeRubricCount === 1 ? '' : 's'} scored
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                  </motion.button>
                )}

                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ ...easeOut, delay: 0.1 }}
                  className="flex items-center gap-3 pt-2"
                >
                  <Button
                    size="sm"
                    className="bg-primary hover:bg-primary/90"
                    onClick={() => applyPrompt(undefined, 'save')}
                  >
                    Save Version
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => applyPrompt(undefined, 'run_ab_test')}
                  >
                    <FlaskConical className="h-3.5 w-3.5 mr-1.5" />
                    Run A/B Tests
                  </Button>
                </motion.div>
              </motion.div>
            )}

            {/* A/B test running spinner */}
            {state.phase === 'ab_running' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-5"
              >
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Applying changes and running A/B tests...
                </div>

                {/* Eval scores sidebar trigger (read-only) */}
                {showWorkflowInsightsSidebar &&
                  ((state.reviewArtifacts?.failure_patterns?.length ?? 0) > 0 ||
                  state.workflowStatus?.baseline_scores?.score_averages) && (
                  <motion.button
                    type="button"
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setSidebarOpen(true)}
                    className="w-full flex items-center gap-3 rounded-xl border border-border/60 bg-card px-4 py-3 text-left hover:bg-muted/30 transition-colors"
                  >
                    <BarChart3 className="h-4 w-4 text-primary shrink-0" />
                    <div className="min-w-0 flex-1">
                      <span className="text-sm font-medium text-heading">
                        View Identified Patterns and Scores
                      </span>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                  </motion.button>
                )}

                {/* Prompt changes sidebar trigger (read-only) */}
                {(state.reviewArtifacts?.proposed_changes?.length ?? 0) > 0 && (
                  <motion.button
                    type="button"
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setChangesOpen(true)}
                    className="w-full flex items-center gap-3 rounded-xl border border-border/60 bg-card px-4 py-3 text-left hover:bg-muted/30 transition-colors"
                  >
                    <Pencil className="h-4 w-4 text-primary shrink-0" />
                    <div className="min-w-0 flex-1">
                      <span className="text-sm font-medium text-heading">
                        View Prompt Changes
                      </span>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {state.reviewArtifacts!.proposed_changes.length} Section{state.reviewArtifacts!.proposed_changes.length !== 1 ? 's' : ''} Modified
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                  </motion.button>
                )}
              </motion.div>
            )}

            {/* A/B test results — ab_results phase */}
            {state.phase === 'ab_results' && state.reviewArtifacts && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={easeOut}
                className="space-y-5"
              >
                {/* Applied confirmation */}
                <div className="rounded-xl border border-muted bg-muted/30 p-4">
                  <p className="text-sm text-body leading-relaxed">
                    Changes have been applied and A/B tests completed.
                  </p>
                </div>

                {/* Eval report sidebar trigger */}
                {state.abTestSummary && (
                  <motion.button
                    type="button"
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setReportOpen(true)}
                    className="w-full flex items-center gap-3 rounded-xl border border-border/60 bg-card px-4 py-3 text-left hover:bg-muted/30 transition-colors"
                  >
                    <FlaskConical className="h-4 w-4 text-primary shrink-0" />
                    <div className="min-w-0 flex-1">
                      <span className="text-sm font-medium text-heading">
                        View Evaluation Report
                      </span>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {state.abTestSummary.per_evaluator?.length ?? 0} evaluator{(state.abTestSummary.per_evaluator?.length ?? 0) !== 1 ? 's' : ''} scored
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                  </motion.button>
                )}

                {/* Eval scores sidebar trigger (read-only) */}
                {showWorkflowInsightsSidebar &&
                  (state.reviewArtifacts.failure_patterns.length > 0 ||
                  state.workflowStatus?.baseline_scores?.score_averages) && (
                  <motion.button
                    type="button"
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setSidebarOpen(true)}
                    className="w-full flex items-center gap-3 rounded-xl border border-border/60 bg-card px-4 py-3 text-left hover:bg-muted/30 transition-colors"
                  >
                    <BarChart3 className="h-4 w-4 text-primary shrink-0" />
                    <div className="min-w-0 flex-1">
                      <span className="text-sm font-medium text-heading">
                        View Identified Patterns and Scores
                      </span>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                  </motion.button>
                )}

                {/* Prompt changes sidebar trigger (read-only) */}
                {state.reviewArtifacts.proposed_changes.length > 0 && (
                  <motion.button
                    type="button"
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setChangesOpen(true)}
                    className="w-full flex items-center gap-3 rounded-xl border border-border/60 bg-card px-4 py-3 text-left hover:bg-muted/30 transition-colors"
                  >
                    <Pencil className="h-4 w-4 text-primary shrink-0" />
                    <div className="min-w-0 flex-1">
                      <span className="text-sm font-medium text-heading">
                        View Prompt Changes
                      </span>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {state.reviewArtifacts.proposed_changes.length} Section{state.reviewArtifacts.proposed_changes.length !== 1 ? 's' : ''} Modified
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                  </motion.button>
                )}

                {/* Final action */}
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ ...easeOut, delay: 0.16 }}
                  className="flex items-center gap-3 pt-2">
                  <Button
                    size="sm"
                    className="bg-primary hover:bg-primary/90"
                    onClick={() => navigate('/agents')}
                  >
                    Looks Good - Done
                  </Button>
                  <Button variant="outline" size="sm" onClick={reset}>
                    Start Over
                  </Button>
                </motion.div>
              </motion.div>
            )}

            {/* Saving spinner */}
            {state.phase === 'saving' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex items-center gap-2 text-sm text-muted-foreground py-4"
              >
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving new version...
              </motion.div>
            )}

            {/* Done card */}
            {state.phase === 'done' && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <VersionCreatedCard
                  versionNumber={state.newVersionNumber}
                  onStartOver={reset}
                />
              </motion.div>
            )}

            {/* Error */}
            {state.phase === 'error' && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="rounded-xl border border-red-200 dark:border-red-800/40 bg-red-50/50 dark:bg-red-950/20 p-4 space-y-3"
              >
                <div>
                  <h4 className="text-sm font-medium text-red-800 dark:text-red-300">
                    Something went wrong
                  </h4>
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                    {state.error || 'An unexpected error occurred.'}
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={reset}>
                  Start Over
                </Button>
              </motion.div>
            )}

            {/* Scroll anchor */}
            <div ref={bottomRef} />
          </div>
        </div>

        {/* ── Pinned input bar ───────────────────────────────────── */}
        {showInput && (
          <div className="border-t border-border/40 px-4 sm:px-6 md:px-8 py-3">
            <div className="max-w-2xl mx-auto">
              <ChatInput
                onSend={sendMessage}
                disabled={state.isSending}
                placeholder="Type your response..."
              />
            </div>
          </div>
        )}
      </div>

      {/* ── Insights Scores Sidebar (from eval overview) ──────── */}
      <EvalScoresSidebar
        open={insightsSidebarOpen}
        onOpenChange={setInsightsSidebarOpen}
        patterns={[]}
        baselineScores={null}
        promptVersion={null}
        evalRubrics={state.evalRubrics}
        evalSetName={state.evalSetName}
      />

      {/* ── Eval Scores Sidebar (portal-rendered Sheet) ──────── */}
      <EvalScoresSidebar
        open={showWorkflowInsightsSidebar && sidebarOpen}
        onOpenChange={setSidebarOpen}
        patterns={showWorkflowInsightsSidebar ? workflowSidebarPatterns : []}
        baselineScores={showWorkflowInsightsSidebar ? workflowSidebarScores : null}
        promptVersion={showWorkflowInsightsSidebar ? (state.reviewArtifacts?.original_prompt_version ?? null) : null}
      />

      {/* ── Prompt Changes Sidebar (portal-rendered Sheet) ──── */}
      <PromptChangesSidebar
        open={changesOpen}
        onOpenChange={setChangesOpen}
        changes={state.reviewArtifacts?.proposed_changes ?? []}
        onSaveEdit={updateChangeText}
        onDelete={removeChange}
        readOnly={state.phase === 'ab_running' || state.phase === 'ab_results'}
      />

      {/* ── Eval Report Sidebar (portal-rendered Sheet) ──── */}
      <EvalReportSidebar
        open={reportOpen}
        onOpenChange={setReportOpen}
        abTestSummary={state.abTestSummary}
      />
    </>
  );
};

const CreateVersionPage: React.FC = () => {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;

  const { data: salesAgent, isLoading } = useQuery({
    queryKey: ['sales-agent', orgId],
    queryFn: async () => {
      if (!orgId) return null;
      const response = await salesAgentService.getSalesAgent(orgId);
      if (response.success && response.data) {
        return response.data;
      }
      return null;
    },
    enabled: !!orgId,
    retry: false,
  });

  if (!currentOrganization || isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!salesAgent) {
    return <BootstrapGenerationPanel organizationId={orgId!} />;
  }

  return <CreateVersionComposer />;
};

export default CreateVersionPage;
